<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("page.category_manager.change_parameter_settings") }}</template>
        <template v-slot:body>
            <nav class="breadcrumb has-succeeds-separator mb-4 is-flex is-align-items-center" v-if="breadcrumb.length > 1">
                <ul>
                    <li v-for="crumb in breadcrumb" :key="crumb.id" :class="{ 'is-active': crumb.id === parameterId }">
                        <a href="#" @click.prevent="navigateToCrumb(crumb.id)">{{ crumb.name }}</a>
                    </li>
                </ul>
                <InlineSpinner v-if="isRefreshing" class="ml-2" />
            </nav>
            <div class="columns is-centered" v-if="showLoading">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
            <div v-else :class="{ 'is-refreshing': isRefreshing }">
                <div class="field" :class="isValidPrettyFightTime ? '' : 'has-error'">
                    <label class="label">
                        {{ translate("page.category_manager.fight_time_format") }}
                        <span v-if="fightTimeField.mode === 'inherited'" class="tag is-info is-light ml-2">
                            {{ translate("page.category_manager.inherited") }}
                        </span>
                    </label>
                    <div class="field has-addons">
                        <div class="control is-expanded">
                            <input
                                type="text"
                                class="input"
                                v-model="prettyFightTime"
                                placeholder="MM:SS"
                                :disabled="fightTimeField.mode === 'inherited'"
                            />
                        </div>
                        <div class="control" v-if="fightTimeField.mode === 'own'">
                            <button class="button" @click="resetFightTime">
                                {{ translate("page.category_manager.reset") }}
                            </button>
                        </div>
                        <div class="control" v-else-if="fightTimeField.mode === 'inherited'">
                            <button class="button" @click="fightTimeField.mode = 'own'">
                                {{ translate("page.category_manager.override") }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="field">
                    <label class="label">
                        {{ translate("page.category_manager.discipline") }}
                        <span v-if="disciplineField.mode === 'inherited'" class="tag is-info is-light ml-2">
                            {{ translate("page.category_manager.inherited") }}
                        </span>
                    </label>
                    <div class="field has-addons">
                        <div class="control is-expanded">
                            <div class="select is-fullwidth">
                                <select v-model="selectedDiscipline" :disabled="disciplineField.mode === 'inherited'">
                                    <option :value="null">-----</option>
                                    <option v-for="discipline in availableDisciplines" :value="discipline" :key="discipline">
                                        {{ translate("discipline_packages.discipline." + discipline) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="control" v-if="disciplineField.mode === 'own'">
                            <button id="resetDiscipline" class="button" @click="resetDiscipline">
                                {{ translate("page.category_manager.reset") }}
                            </button>
                        </div>
                        <div class="control" v-else-if="disciplineField.mode === 'inherited'">
                            <button id="overrideDiscipline" class="button" @click="disciplineField.mode = 'own'">
                                {{ translate("page.category_manager.override") }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="field">
                    <label class="label">
                        {{ translate("page.category_manager.gender_limit") }}
                        <span v-if="genderField.mode === 'inherited'" class="tag is-info is-light ml-2">
                            {{ translate("page.category_manager.inherited") }}
                        </span>
                    </label>
                    <div class="is-flex is-align-items-center is-flex-wrap-wrap" style="gap: 0.5rem">
                        <Radio
                            name="gender"
                            :model-value="selectedGender ?? undefined"
                            @update:model-value="selectedGender = $event as GenderType"
                            initial-value="female"
                            :disabled="genderField.mode === 'inherited'"
                        >
                            {{ translate("gender.female") }}
                        </Radio>
                        <Radio
                            name="gender"
                            :model-value="selectedGender ?? undefined"
                            @update:model-value="selectedGender = $event as GenderType"
                            initial-value="male"
                            :disabled="genderField.mode === 'inherited'"
                        >
                            {{ translate("gender.male") }}
                        </Radio>

                        <button v-if="genderField.mode === 'own'" class="button is-small" @click="resetGender">
                            {{ translate("page.category_manager.reset") }}
                        </button>
                        <button v-else-if="genderField.mode === 'inherited'" class="button is-small" @click="genderField.mode = 'own'">
                            {{ translate("page.category_manager.override") }}
                        </button>
                    </div>
                </div>

                <div class="field" :class="isValidAgeLimit(ageMinField.mode, parameter.ageMin) ? '' : 'has-error'">
                    <label class="label">
                        {{ translate("page.category_manager.age_min_label") }}
                        <span v-if="ageMinField.mode === 'inherited'" class="tag is-info is-light ml-2">
                            {{ translate("page.category_manager.inherited") }}
                        </span>
                    </label>
                    <div class="field has-addons">
                        <div class="control is-expanded">
                            <input class="input" type="text" v-model="parameter.ageMin" :disabled="ageMinField.mode === 'inherited'" />
                        </div>
                        <div class="control" v-if="ageMinField.mode === 'own'">
                            <button class="button" @click="resetAgeMin">
                                {{ translate("page.category_manager.reset") }}
                            </button>
                        </div>
                        <div class="control" v-else-if="ageMinField.mode === 'inherited'">
                            <button class="button" @click="ageMinField.mode = 'own'">
                                {{ translate("page.category_manager.override") }}
                            </button>
                        </div>
                    </div>
                    <p v-if="!isValidAgeLimit(ageMinField.mode, parameter.ageMin)" class="help">
                        {{ translate("page.category_manager.invalid_age") }}
                    </p>
                </div>

                <div class="field" :class="isValidAgeLimit(ageMaxField.mode, parameter.ageMax) ? '' : 'has-error'">
                    <label class="label">
                        {{ translate("page.category_manager.age_max_label") }}
                        <span v-if="ageMaxField.mode === 'inherited'" class="tag is-info is-light ml-2">
                            {{ translate("page.category_manager.inherited") }}
                        </span>
                    </label>
                    <div class="field has-addons">
                        <div class="control is-expanded">
                            <input class="input" type="text" v-model="parameter.ageMax" :disabled="ageMaxField.mode === 'inherited'" />
                        </div>
                        <div class="control" v-if="ageMaxField.mode === 'own'">
                            <button class="button" @click="resetAgeMax">
                                {{ translate("page.category_manager.reset") }}
                            </button>
                        </div>
                        <div class="control" v-else-if="ageMaxField.mode === 'inherited'">
                            <button class="button" @click="ageMaxField.mode = 'own'">
                                {{ translate("page.category_manager.override") }}
                            </button>
                        </div>
                    </div>
                    <p v-if="!isValidAgeLimit(ageMaxField.mode, parameter.ageMax)" class="help">
                        {{ translate("page.category_manager.invalid_age") }}
                    </p>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
                <button class="button is-primary" @click="confirm" :disabled="!formIsValid || isRefreshing">
                    {{ translate("common.ok") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import Radio from "../Common/Radio.vue";
    import { translate } from "../Common/translations";
    import timeFormatter from "../Common/timeFormatter";
    import { handleError } from "../Common/errorHandler";
    import categoriesApi from "../Api/Categories/categories";
    import type { CategoryInheritableValue, CategoryNode, DisciplineType } from "../Api/Categories/categories";
    import disciplinePackagesApi from "../Api/disciplinePackages";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import InlineSpinner from "../Common/InlineSpinner.vue";
    import { successToast } from "../Common/toasts";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import { useModal } from "../Common/useModal";
    import type { GenderType } from "../Common/Competitors/types";

    type FieldMode = "own" | "inherited" | "unset";

    interface FieldState<T> {
        initialMode: FieldMode;
        mode: FieldMode;
        ancestorValue: T | null;
    }

    function emptyFieldState<T>(): FieldState<T> {
        return { initialMode: "unset", mode: "unset", ancestorValue: null };
    }

    function deriveMode<T>(field: CategoryInheritableValue<T>): FieldMode {
        if (field.inherited) {
            return "inherited";
        }
        return field.value !== null ? "own" : "unset";
    }

    const store = useCategoriesManagerStore();
    const { open: openModal, close, modal } = useModal();

    const parameterId = ref(0);
    const parameterName = ref("");
    const prettyFightTime = ref("");
    const parameter = ref<{ ageMin: number | null; ageMax: number | null }>({
        ageMin: null,
        ageMax: null,
    });
    const showLoading = ref(true);
    const isRefreshing = ref(false);
    const availableDisciplines = ref<DisciplineType[]>([]);
    const selectedDiscipline = ref<DisciplineType | null>(null);
    const selectedGender = ref<GenderType | null>(null);

    const fightTimeField = ref<FieldState<number>>(emptyFieldState());
    const disciplineField = ref<FieldState<DisciplineType>>(emptyFieldState());
    const genderField = ref<FieldState<GenderType>>(emptyFieldState());
    const ageMinField = ref<FieldState<number>>(emptyFieldState());
    const ageMaxField = ref<FieldState<number>>(emptyFieldState());

    // Captured once on open() and left unchanged while navigating, so crumbs for nodes below
    // the currently active one stay visible and clickable (browser-back-style navigation).
    const breadcrumb = ref<CategoryNode[]>([]);

    const formIsValid = computed(() => {
        return (
            isValidPrettyFightTime.value &&
            isValidAgeLimit(ageMinField.value.mode, parameter.value.ageMin) &&
            isValidAgeLimit(ageMaxField.value.mode, parameter.value.ageMax)
        );
    });

    const isValidPrettyFightTime = computed(() => {
        if (fightTimeField.value.mode === "inherited") {
            return true;
        }
        if (fightTimeField.value.mode === "unset" && prettyFightTime.value.length === 0) {
            return true;
        }
        if (prettyFightTime.value.length === 0) {
            return false;
        }
        return prettyFightTime.value.match(/^[0-9]{1,2}:[0-9]{2}$/) !== null;
    });

    function open(parameterIdToOpen: number) {
        openModal();
        breadcrumb.value = store.getBreadcrumbForId(parameterIdToOpen);
        loadParameter(parameterIdToOpen, { isInitialLoad: true });
    }

    // On navigation between crumbs the form stays mounted (dimmed + InlineSpinner) instead of
    // swapping to the full-page spinner, so the modal doesn't blink between an empty and a
    // populated state on every quick re-fetch. Field refs are only overwritten once
    // fetchParameterData resolves, never reset upfront, so old values stay visible while loading.
    function loadParameter(parameterIdToOpen: number, options: { isInitialLoad?: boolean } = {}) {
        parameterId.value = parameterIdToOpen;
        if (options.isInitialLoad) {
            showLoading.value = true;
        } else {
            isRefreshing.value = true;
        }

        Promise.all([fetchParameterData(parameterIdToOpen), fetchAvailableDisciplines()])
            .catch(handleError)
            .finally(() => {
                showLoading.value = false;
                isRefreshing.value = false;
            });
    }

    function navigateToCrumb(id: number) {
        if (id === parameterId.value) {
            return;
        }
        loadParameter(id);
    }

    function confirm() {
        showLoading.value = true;
        return updateParameterData()
            .then(() => {
                close();
                successToast(translate("page.category_manager.category_updated"));
            })
            .then(() => {
                return store.refreshCategories();
            })
            .catch(handleError)
            .finally(() => {
                showLoading.value = false;
            });
    }

    function resetFightTime() {
        fightTimeField.value.mode = fightTimeField.value.ancestorValue !== null ? "inherited" : "unset";
        prettyFightTime.value =
            fightTimeField.value.ancestorValue !== null
                ? timeFormatter.formatSecondsToMinutesAndSeconds(fightTimeField.value.ancestorValue)
                : "";
    }

    function resetDiscipline() {
        disciplineField.value.mode = disciplineField.value.ancestorValue !== null ? "inherited" : "unset";
        selectedDiscipline.value = disciplineField.value.ancestorValue;
    }

    function resetGender() {
        genderField.value.mode = genderField.value.ancestorValue !== null ? "inherited" : "unset";
        selectedGender.value = genderField.value.ancestorValue;
    }

    function resetAgeMin() {
        ageMinField.value.mode = ageMinField.value.ancestorValue !== null ? "inherited" : "unset";
        parameter.value.ageMin = ageMinField.value.ancestorValue;
    }

    function resetAgeMax() {
        ageMaxField.value.mode = ageMaxField.value.ancestorValue !== null ? "inherited" : "unset";
        parameter.value.ageMax = ageMaxField.value.ancestorValue;
    }

    function fetchAvailableDisciplines(): Promise<void> {
        if (availableDisciplines.value.length > 0) {
            return Promise.resolve();
        }
        return disciplinePackagesApi.getDisciplineMetadata().then(metadata => {
            availableDisciplines.value = Object.keys(metadata.disciplines) as DisciplineType[];
        });
    }

    function fetchParameterData(parameterIdToFetch: number) {
        return categoriesApi.getCategoryMetadata(store.eventId, parameterIdToFetch).then(metadata => {
            parameterId.value = metadata.id;
            parameterName.value = metadata.name;

            fightTimeField.value = {
                initialMode: deriveMode(metadata.fightTime),
                mode: deriveMode(metadata.fightTime),
                ancestorValue: metadata.fightTime.ancestorValue,
            };
            prettyFightTime.value =
                metadata.fightTime.value !== null ? timeFormatter.formatSecondsToMinutesAndSeconds(metadata.fightTime.value) : "";

            disciplineField.value = {
                initialMode: deriveMode(metadata.discipline),
                mode: deriveMode(metadata.discipline),
                ancestorValue: metadata.discipline.ancestorValue,
            };
            selectedDiscipline.value = metadata.discipline.value;

            genderField.value = {
                initialMode: deriveMode(metadata.gender),
                mode: deriveMode(metadata.gender),
                ancestorValue: metadata.gender.ancestorValue,
            };
            selectedGender.value = metadata.gender.value;

            ageMinField.value = {
                initialMode: deriveMode(metadata.ageMin),
                mode: deriveMode(metadata.ageMin),
                ancestorValue: metadata.ageMin.ancestorValue,
            };
            parameter.value.ageMin = metadata.ageMin.value;

            ageMaxField.value = {
                initialMode: deriveMode(metadata.ageMax),
                mode: deriveMode(metadata.ageMax),
                ancestorValue: metadata.ageMax.ancestorValue,
            };
            parameter.value.ageMax = metadata.ageMax.value;
        });
    }

    function updateParameterData() {
        const paramsToClear: string[] = [];
        if (fightTimeField.value.initialMode === "own" && fightTimeField.value.mode !== "own") {
            paramsToClear.push("fightTime");
        }
        if (disciplineField.value.initialMode === "own" && disciplineField.value.mode !== "own") {
            paramsToClear.push("discipline");
        }
        if (genderField.value.initialMode === "own" && genderField.value.mode !== "own") {
            paramsToClear.push("gender");
        }
        if (ageMinField.value.initialMode === "own" && ageMinField.value.mode !== "own") {
            paramsToClear.push("ageMin");
        }
        if (ageMaxField.value.initialMode === "own" && ageMaxField.value.mode !== "own") {
            paramsToClear.push("ageMax");
        }

        let prettyFightTimeAsSeconds: number | null = null;
        if (fightTimeField.value.mode !== "inherited" && prettyFightTime.value.length > 0) {
            prettyFightTimeAsSeconds = timeFormatter.formatToSeconds(prettyFightTime.value);
            if (prettyFightTimeAsSeconds === 0) {
                prettyFightTimeAsSeconds = null;
            }
        }

        const performUpdate = () =>
            categoriesApi.updateParameter(store.eventId, parameterId.value, parameterName.value, {
                fightTime: prettyFightTimeAsSeconds,
                discipline: disciplineField.value.mode !== "inherited" ? selectedDiscipline.value : null,
                ageMin: ageMinField.value.mode !== "inherited" && parameter.value.ageMin !== null ? Number(parameter.value.ageMin) : null,
                ageMax: ageMaxField.value.mode !== "inherited" && parameter.value.ageMax !== null ? Number(parameter.value.ageMax) : null,
                gender: genderField.value.mode !== "inherited" ? selectedGender.value : null,
            });

        if (paramsToClear.length === 0) {
            return performUpdate();
        }

        return categoriesApi.clearParams(store.eventId, parameterId.value, paramsToClear).then(performUpdate);
    }

    function isValidAgeLimit(mode: FieldMode, ageLimit: number | null | undefined | string) {
        if (mode === "inherited") {
            return true;
        }

        if (ageLimit === "") {
            return false;
        }

        if (!ageLimit) {
            return true;
        }

        if ((ageLimit as number) < 1) {
            return false;
        }
        if ((ageLimit as number) >= 100) {
            return false;
        }

        return ("" + ageLimit || "").match("^[0-9]+$");
    }

    defineExpose({
        open,
    });
</script>

<style scoped>
    .is-refreshing {
        opacity: 0.5;
        pointer-events: none;
        transition: opacity 0.15s ease;
    }
</style>
